import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { ApiConfig } from "@/config/api.config";
import { ApiHookProps } from '@/@types/api.types';
import { SettingsDto, UpdateSettingsDto } from '@/features/settings/types/settings-dto';
import EToast from '@/components/everridge/EToast';
import { t } from 'i18next';
import { useAxiosAuth } from '@/hooks/useAxiosAuth';

type Props = {} & ApiHookProps;

export const useSettingsApi = ({}: Props = {}) => {
    const queryClient = useQueryClient();
    const axiosInstance = useAxiosAuth();

    const {
        data: settingsData, 
        isLoading: isLoadingSettingsData, 
        isError: isErrorSettingsData,    
    } = useQuery({
        queryKey: [ApiConfig.queries.features.settings.queryKeys.details],
        queryFn: async (): Promise<SettingsDto> => {
            const url = ApiConfig.queries.features.settings.endpoints.get;
            const response = await axiosInstance.get<SettingsDto>(url);
            return response.data;
        },       
    });

    const {
        mutateAsync: updateSettings, 
        isPending: isUpdatingingSettings, 
        isError: isErrorUpdatingSettings
    } = useMutation({
        mutationFn: async (dto: UpdateSettingsDto) => {
            const url = ApiConfig.queries.features.settings.endpoints.update;
            const response = await axiosInstance.put(url, dto);
            return response.data;

        },
        onSuccess: async () => {
            await queryClient.invalidateQueries({
                    queryKey: [ApiConfig.queries.features.settings.queryKeys.details]
                });
            await queryClient.invalidateQueries({
                queryKey: [ApiConfig.queries.features.finances.queryKeys.rentAnalyticsExpenseVisibility],
            });
            await queryClient.invalidateQueries({
                queryKey: [ApiConfig.queries.features.finances.queryKeys.analyticsMonthly],
            });
            EToast({
                variant: 'success', 
                message: t('main.settings.toasts.save.success.message'), 
                description: t('main.settings.toasts.save.success.description')
            });
        },
        onError: () => {
            EToast({
                variant: 'error', 
                message: t('main.settings.toasts.save.error.message'), 
                description: t('main.settings.toasts.save.error.description')
            });
        }
    });

    const {
        mutateAsync: uploadSettingsLogo,
        isPending: isUploadingSettingsLogo,
    } = useMutation({
        mutationFn: async (file: File) => {
            const formData = new FormData();
            formData.append('file', file);

            await axiosInstance.put(
                ApiConfig.queries.features.settings.endpoints.uploadLogo,
                formData,
                {
                    headers: {
                        'Content-Type': 'multipart/form-data',
                    },
                },
            );
        },
        onSuccess: async () => {
            await queryClient.invalidateQueries({
                queryKey: [ApiConfig.queries.features.settings.queryKeys.details],
            });
            await queryClient.invalidateQueries({
                queryKey: [ApiConfig.queries.auth.queryKeys.tenants],
            });
        },
    });

    return {
        settingsData,
        isLoadingSettingsData,
        isErrorSettingsData,
        updateSettings,
        isUpdatingingSettings,
        isErrorUpdatingSettings,
        uploadSettingsLogo,
        isUploadingSettingsLogo,
    }
};
